import {
  ORGANIZATION_ASSISTANT_PAGE_PATH,
  ORGANIZATION_ASSISTANT_PAGE_PATH_MAX_LENGTH,
  ORGANIZATION_ASSISTANT_PAGE_PAYOUT_ID,
} from "@simplyk/common";
import { z } from "zod";

export interface OrganizationAssistantPage {
  path: string;
  formId?: string;
  transactionId?: string;
  payoutId?: string;
}

const ORGANIZATION_ROOT_SEGMENT = "o";
const FORM_ID_PARAMS = ["formId", "donationFormId", "ticketingId"] as const;
const UuidSchema = z.string().uuid();

export const buildOrganizationAssistantPage = (
  pathWithoutLocale: string,
  searchParams: Pick<URLSearchParams, "get"> | null
): OrganizationAssistantPage => {
  const formId = FORM_ID_PARAMS.map((name) => searchParams?.get(name)).find(isUuid);
  const transactionId = searchParams?.get("transactionId");
  const payoutId = searchParams?.get("payoutId");
  return {
    path: toPagePath(pathWithoutLocale),
    ...(formId !== undefined && { formId }),
    ...(isUuid(transactionId) && { transactionId }),
    ...(isStripePayoutId(payoutId) && { payoutId }),
  };
};

const isUuid = (value: string | null | undefined): value is string => UuidSchema.safeParse(value).success;

const isStripePayoutId = (value: string | null | undefined): value is string =>
  typeof value === "string" && ORGANIZATION_ASSISTANT_PAGE_PAYOUT_ID.test(value);

const toPath = (segments: string[]) => ["", ORGANIZATION_ROOT_SEGMENT, ...segments].join("/");

const isAcceptedPath = (path: string) =>
  ORGANIZATION_ASSISTANT_PAGE_PATH.test(path) && path.length <= ORGANIZATION_ASSISTANT_PAGE_PATH_MAX_LENGTH;

const toPagePath = (pathWithoutLocale: string): string => {
  const [root, ...segments] = pathWithoutLocale.split("/").filter(Boolean);
  if (root !== ORGANIZATION_ROOT_SEGMENT) {
    return toPath([]);
  }
  const refusedIndex = segments.findIndex((_, index) => !isAcceptedPath(toPath(segments.slice(0, index + 1))));
  return toPath(refusedIndex < 0 ? segments : segments.slice(0, refusedIndex));
};
